import React, { useState, useRef, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';

type ColumnResizerProps = {
  onResize: (width: number) => void;
  initialWidth?: number;
  minWidth?: number;
};

const ColumnResizer = ({ onResize, initialWidth, minWidth = 50 }: ColumnResizerProps) => {
  const theme = useTheme();
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(initialWidth || 0);
  const [contentMinWidth, setContentMinWidth] = useState(minWidth);
  const [hasText, setHasText] = useState(true);
  const resizerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const checkText = React.useCallback(() => {
    if (resizerRef.current?.parentElement) {
      const textContent =
        resizerRef.current.parentElement.textContent || resizerRef.current.parentElement.innerText || '';
      const hasTextContent = !!textContent.trim();
      setHasText(hasTextContent);
    }
  }, []);

  useEffect(() => {
    // Initial check after mount
    const timeoutId = setTimeout(checkText, 0);

    // Check periodically for dynamic text changes
    const interval = setInterval(checkText, 200);

    // Setup mutation observer
    const setupObserver = () => {
      if (resizerRef.current?.parentElement && !observerRef.current) {
        observerRef.current = new MutationObserver(checkText);
        observerRef.current.observe(resizerRef.current.parentElement, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    };

    const observerTimeoutId = setTimeout(setupObserver, 100);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(observerTimeoutId);
      clearInterval(interval);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [checkText]);

  // Also check when ref is set using callback ref
  const setResizerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      resizerRef.current = node;
      if (node?.parentElement) {
        checkText();
      }
    },
    [checkText],
  );

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(contentMinWidth, startWidth + diff);
      onResize(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, startX, startWidth, contentMinWidth, onResize]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!resizerRef.current?.parentElement) {
      return;
    }

    const parentCell = resizerRef.current.parentElement;

    // Check dynamically if header has text content (handles dynamic text changes)
    const textContent = parentCell.textContent || parentCell.innerText || '';
    if (!textContent.trim()) {
      return;
    }

    setIsResizing(true);
    setStartX(e.clientX);
    const parentWidth = parentCell.getBoundingClientRect().width;
    setStartWidth(parentWidth);

    // Calculate minimum width based on header text content
    const tempSpan = document.createElement('span');
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.style.whiteSpace = 'nowrap';
    tempSpan.style.fontSize = window.getComputedStyle(parentCell).fontSize;
    tempSpan.style.fontFamily = window.getComputedStyle(parentCell).fontFamily;
    tempSpan.style.fontWeight = window.getComputedStyle(parentCell).fontWeight;
    tempSpan.style.padding = window.getComputedStyle(parentCell).padding;
    tempSpan.textContent = textContent.trim();

    document.body.appendChild(tempSpan);
    const contentWidth = tempSpan.offsetWidth;
    document.body.removeChild(tempSpan);

    // Add some padding for the resizer and cell padding
    const calculatedMinWidth = Math.max(minWidth, contentWidth + 16);
    setContentMinWidth(calculatedMinWidth);
  };

  if (!hasText) {
    return null;
  }

  return (
    <Box
      ref={setResizerRef}
      onMouseDown={handleMouseDown}
      sx={{
        position: 'absolute',
        right: '-5px',
        top: 0,
        bottom: 0,
        width: '10px',
        cursor: 'col-resize',
        zIndex: 10,
        backgroundColor: 'transparent',
        // Theme-aware line; slightly stronger on hover
        '&::before': {
          content: '""',
          position: 'absolute',
          left: '50%',
          top: 0,
          bottom: 0,
          width: '1px',
          transform: 'translateX(-50%)',
          backgroundColor: theme.palette.divider,
          opacity: 0.8,
          transition: 'opacity 0.15s ease',
        },
        '&:hover::before': {
          opacity: 1,
        },
      }}
    />
  );
};

export default ColumnResizer;
